import type { CSSProperties } from 'react';
import { WEBSITE_SCHEMES, type WebsiteScheme } from '@bulk/types';
import type { Branding, ColorRef, ColorToken, SiteDesign } from '@bulk/validation';
import { readableColor } from '@/lib/utils';

interface Scheme {
  background: ColorRef;
  text: ColorRef;
  accent: ColorRef;
}

const BUILT_IN: Record<WebsiteScheme, Scheme> = {
  default: { background: 'background', text: 'text', accent: 'primary' },
  muted: { background: 'surface', text: 'text', accent: 'primary' },
  inverse: { background: 'text', text: 'background', accent: 'primary' },
  brand: { background: 'primary', text: 'onPrimary', accent: 'secondary' },
  dark: { background: 'secondary', text: 'onSecondary', accent: 'primary' },
  light: { background: 'white', text: 'black', accent: 'primary' },
};

export function isScheme(value: string): value is WebsiteScheme {
  return (WEBSITE_SCHEMES as readonly string[]).includes(value);
}

function rgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  const n = m ? parseInt(m[1]!, 16) : 0;
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex([r, g, b]: [number, number, number]): string {
  return `#${[r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`;
}

export function mixHex(a: string, b: string, weightOfA: number): string {
  const [ar, ag, ab] = rgb(a);
  const [br, bg, bb] = rgb(b);
  return toHex([ar * weightOfA + br * (1 - weightOfA), ag * weightOfA + bg * (1 - weightOfA), ab * weightOfA + bb * (1 - weightOfA)]);
}

function luminance(hex: string): number {
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = rgb(hex).map(channel) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (hi + 0.05) / (lo + 0.05);
}

export interface SchemeColors {
  background: string;
  text: string;
  accent: string;
  accentText: string;
}

/**
 * The concrete colours of a scheme for this gym: the design's own definition when it has one under that key,
 * otherwise the built-in one, and `default` for a key neither knows. An accent that would disappear into the
 * background it sits on (a yellow button on a yellow band) falls back to the next brand colour that shows,
 * then to the text colour.
 */
export function schemeColors(scheme: string, brand: Branding, design: SiteDesign | null = null): SchemeColors {
  const def: Scheme = design?.schemes[scheme] ?? (isScheme(scheme) ? BUILT_IN[scheme] : (design?.schemes['default'] ?? BUILT_IN.default));
  const tokens: Record<ColorToken, string> = {
    primary: brand.primaryColor,
    secondary: brand.secondaryColor,
    background: brand.backgroundColor,
    text: brand.textColor,
    surface: mixHex(brand.textColor, brand.backgroundColor, 0.06),
    onPrimary: readableColor(brand.primaryColor),
    onSecondary: readableColor(brand.secondaryColor),
    white: '#ffffff',
    black: '#111111',
  };
  const color = (ref: ColorRef) => (ref.startsWith('#') ? ref : tokens[ref as ColorToken]);
  const background = color(def.background);
  const text = color(def.text);
  const accent = [color(def.accent), brand.primaryColor, brand.secondaryColor, text].find((c) => contrast(c, background) >= 1.6) ?? text;
  return { background, text, accent, accentText: readableColor(accent) };
}

export function schemeStyle(scheme: string, brand: Branding, design: SiteDesign | null = null): CSSProperties {
  const c = schemeColors(scheme, brand, design);
  return {
    '--brand-primary': c.accent,
    '--brand-primary-foreground': c.accentText,
    '--brand-secondary': brand.secondaryColor,
    '--brand-bg': c.background,
    '--brand-text': c.text,
    '--color-brand': c.accent,
    '--color-brand-foreground': c.accentText,
    '--brand-line': `color-mix(in srgb, ${c.text} 14%, transparent)`,
    '--brand-muted': `color-mix(in srgb, ${c.text} 68%, transparent)`,
    '--brand-surface': `color-mix(in srgb, ${c.text} 6%, ${c.background})`,
    '--brand-surface-strong': `color-mix(in srgb, ${c.text} 12%, ${c.background})`,
    '--brand-nav': `color-mix(in srgb, ${c.background} 86%, transparent)`,
    backgroundColor: c.background,
    color: c.text,
  } as CSSProperties;
}

export function siteStyle(brand: Branding, design: SiteDesign | null = null): CSSProperties {
  const font = brand.fontFamily.replace(/["']/g, '').trim();
  return { ...schemeStyle('default', brand, design), fontFamily: font && !design?.typography.body ? `'${font}', var(--font-sans)` : undefined };
}
